// /var/www/oszczedzam/client/src/utils/goalUtils.js
/**
 * Zbiór funkcji pomocniczych do obsługi celów oszczędnościowych
 */

/**
 * Formatuj kwotę w formacie walutowym
 * @param {number} amount - Kwota do sformatowania
 * @param {string} currency - Waluta (domyślnie PLN)
 * @returns {string} Sformatowana kwota
 */
export const formatAmount = (amount, currency = 'PLN') => {
    return amount.toLocaleString('pl-PL', { 
      style: 'currency', 
      currency 
    });
  };
  
  /**
   * Formatuj procentową wartość
   * @param {number} percentage - Wartość procentowa
   * @param {number} decimals - Liczba miejsc po przecinku
   * @returns {string} Sformatowany procent
   */
  export const formatPercentage = (percentage, decimals = 1) => {
    return percentage.toFixed(decimals) + '%';
  };
  
  /**
   * Formatuj datę w czytelnym formacie
   * @param {string|Date} date - Data do sformatowania
   * @param {string} format - Format daty (short, medium, long)
   * @returns {string} Sformatowana data
   */
  export const formatDate = (date, format = 'medium') => {
    if (!date) return 'Brak daty';
    
    const dateObj = date instanceof Date ? date : new Date(date);
    let options = {};
    
    switch(format) {
      case 'short':
        options = { day: '2-digit', month: '2-digit', year: 'numeric' };
        break;
      case 'long':
        options = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };
        break;
      case 'month':
        options = { month: 'long', year: 'numeric' };
        break;
      case 'medium':
      default:
        options = { day: 'numeric', month: 'short', year: 'numeric' };
    }
    
    return dateObj.toLocaleDateString('pl-PL', options);
  };
  
  /**
   * Oblicz czas pozostały do osiągnięcia celu
   * @param {string|Date} targetDate - Data docelowa
   * @returns {string} Sformatowany czas pozostały
   */
  export const calculateTimeToGoal = (targetDate) => {
    if (!targetDate) return 'Brak daty docelowej';
    
    const today = new Date();
    const target = new Date(targetDate);
    
    // Jeśli data już minęła
    if (target < today) return 'Termin minął';
    
    const diffTime = Math.abs(target - today);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 30) {
      return `Zostało ${diffDays} dni`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `Zostało ${months} ${months === 1 ? 'miesiąc' : months < 5 ? 'miesiące' : 'miesięcy'}`;
    } else {
      const years = Math.floor(diffDays / 365);
      const months = Math.floor((diffDays % 365) / 30);
      return `Zostało ${years} ${years === 1 ? 'rok' : years < 5 ? 'lata' : 'lat'}${months > 0 ? ` i ${months} ${months === 1 ? 'miesiąc' : months < 5 ? 'miesiące' : 'miesięcy'}` : ''}`;
    }
  };
  
  /**
   * Oblicz sugerowaną miesięczną kwotę oszczędności potrzebną do osiągnięcia celu
   * @param {number} targetAmount - Kwota docelowa
   * @param {number} currentAmount - Aktualna kwota
   * @param {string|Date} targetDate - Data docelowa
   * @returns {number} Sugerowana kwota miesięczna
   */
  export const calculateSuggestedMonthlySaving = (targetAmount, currentAmount, targetDate) => {
    if (!targetDate || !targetAmount || parseFloat(targetAmount) <= 0) return 0;
    
    const today = new Date();
    const target = new Date(targetDate);
    
    // Jeśli data już minęła
    if (target <= today) return 0;
    
    const diffTime = Math.abs(target - today);
    const diffMonths = Math.max(1, Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 30)));
    
    const remaining = targetAmount - currentAmount;
    return remaining <= 0 ? 0 : remaining / diffMonths;
  };
  
  /**
   * Oblicz szacowany czas osiągnięcia celu na podstawie historii oszczędności
   * @param {Object} goal - Obiekt celu oszczędnościowego
   * @returns {string} Szacowana data osiągnięcia lub informacja
   */
  export const estimateCompletionDate = (goal) => {
    if (!goal.savingsHistory || goal.savingsHistory.length < 2) {
      return 'Brak wystarczających danych';
    }
    
    // Oblicz średnią miesięczną wpłatę
    const history = [...goal.savingsHistory].sort((a, b) => new Date(a.date) - new Date(b.date));
    const firstDate = new Date(history[0].date);
    const lastDate = new Date(history[history.length - 1].date);
    
    // Oblicz całkowity czas w dniach
    const totalDays = Math.max(1, Math.ceil((lastDate - firstDate) / (1000 * 60 * 60 * 24)));
    
    // Oblicz sumę wszystkich dodatnich wpłat
    const totalSaved = history
      .filter(item => item.amount > 0)
      .reduce((sum, item) => sum + item.amount, 0);
    
    // Oblicz średnią dzienną wpłatę
    const avgDailySaving = totalSaved / totalDays;
    
    if (avgDailySaving <= 0) return 'Brak postępu w oszczędzaniu';
    
    // Oblicz pozostałą kwotę
    const remaining = goal.targetAmount - goal.currentAmount;
    
    if (remaining <= 0) return 'Cel osiągnięty';
    
    // Oblicz szacowaną liczbę dni do osiągnięcia celu
    const daysToCompletion = Math.ceil(remaining / avgDailySaving);
    
    // Oblicz szacowaną datę osiągnięcia
    const completionDate = new Date();
    completionDate.setDate(completionDate.getDate() + daysToCompletion);
    
    return formatDate(completionDate, 'long');
  };
  
  /**
   * Pobierz kolor dla danego priorytetu
   * @param {string} priority - Priorytet (high, medium, low)
   * @returns {string} Nazwa koloru zgodna z Material UI
   */
  export const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };
  
  /**
   * Pobierz etykietę priorytetu w języku polskim
   * @param {string} priority - Priorytet (high, medium, low)
   * @returns {string} Etykieta priorytetu
   */
  export const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'high': return 'Wysoki';
      case 'medium': return 'Średni';
      case 'low': return 'Niski';
      default: return 'Nieokreślony';
    }
  };
  
  /**
   * Oblicz postęp osiągnięcia celu jako procent
   * @param {number} currentAmount - Aktualna kwota
   * @param {number} targetAmount - Kwota docelowa
   * @returns {number} Procent realizacji celu (0-100)
   */
  export const calculateProgress = (currentAmount, targetAmount) => {
    if (!targetAmount || targetAmount <= 0) return 0;
    return Math.min(Math.round((currentAmount / targetAmount) * 100), 100);
  };
  
  /**
   * Pobierz kolor dla paska postępu w zależności od stopnia realizacji
   * @param {number} percent - Procent realizacji (0-100)
   * @returns {string} Nazwa koloru zgodna z Material UI
   */
  export const getProgressColor = (percent) => {
    if (percent >= 100) return 'success';
    if (percent >= 75) return 'info';
    if (percent >= 50) return 'primary';
    if (percent >= 25) return 'warning';
    return 'error';
  };
  
  /**
   * Generuj podsumowanie postępów dla wszystkich celów
   * @param {Array} goals - Lista celów oszczędnościowych
   * @returns {Object} Obiekt z podsumowaniem
   */
  export const generateGoalsSummary = (goals) => {
    if (!goals || goals.length === 0) {
      return {
        totalGoals: 0,
        completedGoals: 0,
        totalSaved: 0,
        totalTarget: 0,
        averageProgress: 0
      };
    }
    
    const completedGoals = goals.filter(goal => goal.currentAmount >= goal.targetAmount);
    const totalSaved = goals.reduce((sum, goal) => sum + goal.currentAmount, 0);
    const totalTarget = goals.reduce((sum, goal) => sum + goal.targetAmount, 0);
    
    // Średni postęp dla wszystkich celów
    const sumProgress = goals.reduce((sum, goal) => {
      const progress = (goal.currentAmount / goal.targetAmount) * 100;
      return sum + (isNaN(progress) ? 0 : progress);
    }, 0);
    
    return {
      totalGoals: goals.length,
      completedGoals: completedGoals.length,
      totalSaved,
      totalTarget,
      averageProgress: Math.round(sumProgress / goals.length) || 0
    };
  };